import { canClinical, type ClinicalCapability } from '../core/permissions/clinical-capabilities';
import { MenuItem } from 'primeng/api';
import * as Roles from '../core/permissions/role-groups';
import { SIDEBAR_MODULES } from '../core/config/sidebar-modules';

interface NavigationItem extends MenuItem { itemRoles?: string[]; }

interface NavigationContext {
  t: (key: string) => string;
  hasAnyRole: (roles: string[]) => boolean;
  itemAllowed: (item: MenuItem & { itemRoles?: string[] }) => boolean;
  enabledModules?: string[];
  role?: string;
}

/** Qui voit l'espace avant même de regarder ses entrées. */
type Audience = 'all' | 'pharmacy' | 'emergency' | 'finance' | 'hr' | 'admin' | 'super_admin';

interface NavigationEntry {
  route: string;
  /** Clé i18n du libellé. */
  label: string;
  icon: string;
  /** Rôles de l'entrée (groupes de `core/permissions/role-groups.ts`). */
  roles: readonly string[];
  /** Module (Paramètres > Modules) de l'entrée ; à défaut, celui de l'espace. */
  moduleKey?: string;
  /** Clé i18n de l'intertitre qui ouvre un sous-groupe. */
  groupStart?: string;
  queryParams?: (role?: string) => Record<string, string>;
  /** Droit clinique requis en plus des rôles (même règle que l'API). */
  capability?: ClinicalCapability;
}

interface NavigationSpace {
  /** Identifiant stable (ancre DOM du panneau), indépendant de la langue. */
  id: string;
  /** Clé `NAV.SPACES.*`. */
  label: string;
  icon: string;
  audience: Audience;
  /** Module qui porte l'espace entier ; absent quand les entrées dépendent chacune du leur. */
  moduleKey?: string;
  entries: readonly NavigationEntry[];
}

const { CLINICAL, DOCTOR_ONLY, LAB, PHARMACY } = Roles;

/**
 * Les espaces du menu, dans l'ordre d'affichage. Le plafond est de six entrées
 * par espace pour n'importe quel rôle, tous modules activés (voir le spec).
 */
const SPACES: readonly NavigationSpace[] = [
  {
    id: 'home', label: 'NAV.SPACES.HOME', icon: 'pi pi-home', audience: 'all', moduleKey: 'vue-globale',
    entries: [
      { route: '/dashboard', label: 'NAV.MY_DASHBOARD', icon: 'pi pi-th-large', roles: Roles.STAFF_ALL },
      { route: '/alerts', label: 'NAV.OPERATIONAL_ALERTS', icon: 'pi pi-bell', roles: Roles.OPERATIONS },
      { route: '/calendar', label: 'NAV.MY_SCHEDULE', icon: 'pi pi-calendar-clock', roles: Roles.STAFF_ALL },
      // Hub unique : analytique, financier, médical, performance, exports, épidémiologie.
      { route: '/reports', label: 'NAV.REPORTS', icon: 'pi pi-chart-bar', roles: Roles.REPORTS },
    ],
  },
  {
    // Le patient, de son arrivée à l'acte : accueil, dossier, rendez-vous,
    // consultation, vaccination.
    id: 'patients', label: 'NAV.SPACES.PATIENTS', icon: 'pi pi-users', audience: 'all', moduleKey: 'patients-soins',
    entries: [
      {
        // File d'attente + Pré-enregistrements + Salles d'attente (+ vue en ligne).
        route: '/reception-hub', label: 'NAV.RECEPTION', icon: 'pi pi-list-check', roles: Roles.STAFF_ALL,
        queryParams: (role) => role === 'gestionnaire' ? { tab: 'en-ligne' }
          : role === 'doctor' ? { tab: 'file', view: 'pipeline' } : { tab: 'file' },
      },
      // Vues Aperçu / Dossiers / Suivi chronique / Carnets sur la même page.
      { route: '/patients', label: 'NAV.PATIENTS', icon: 'pi pi-users', roles: Roles.PATIENT_MGMT },
      { route: '/appointments', label: 'NAV.APPOINTMENTS', icon: 'pi pi-calendar', roles: Roles.COORDINATION },
      // Consultations + Téléconsultations + Ordonnances + Arrêts maladie.
      { route: '/consultations-hub', label: 'NAV.CONSULTATIONS', icon: 'pi pi-comments', roles: Roles.CONSULTATIONS_HUB },
      { route: '/referrals', label: 'NAV.REFERRALS', icon: 'pi pi-directions-alt', roles: DOCTOR_ONLY },
      // Actes de vaccination + Certificats : le carnet vaccinal du patient.
      { route: '/vaccinations-hub', label: 'NAV.VACCINATIONS', icon: 'pi pi-shield', roles: CLINICAL, moduleKey: 'hospitalisation' },
    ],
  },
  {
    id: 'care', label: 'NAV.SPACES.CARE', icon: 'pi pi-heart', audience: 'all',
    entries: [
      { route: '/hospitalization', label: 'NAV.HOSPITALIZATION', icon: 'pi pi-building', roles: CLINICAL, moduleKey: 'hospitalisation', capability: 'hospitalizationRead' },
      { route: '/nursing-care', label: 'NAV.NURSING_CARE', icon: 'pi pi-heart', roles: CLINICAL, moduleKey: 'hospitalisation' },
      // Planning + Interventions + Salles + Équipements chirurgicaux.
      { route: '/bloc-hub', label: 'NAV.OPERATING_THEATRE', icon: 'pi pi-wrench', roles: DOCTOR_ONLY, moduleKey: 'hospitalisation' },
      { route: '/adverse-events', label: 'NAV.ADVERSE_EVENTS', icon: 'pi pi-exclamation-triangle', roles: CLINICAL, moduleKey: 'hospitalisation' },
      // La transfusion relève du module Hospitalisation dans la configuration existante :
      // une clé dédiée l'aurait rendue invisible aux organisations déjà configurées.
      { route: '/blood-hub', label: 'NAV.TRANSFUSION', icon: 'pi pi-heart-fill', roles: Roles.TRANSFUSION, moduleKey: 'hospitalisation' },
      // Tableau + Grossesses + Visites prénatales + Accouchements + Nouveau-nés.
      { route: '/maternity-hub', label: 'NAV.MATERNITY', icon: 'pi pi-home', roles: CLINICAL, moduleKey: 'maternite' },
    ],
  },
  {
    id: 'emergency', label: 'NAV.SPACES.EMERGENCY', icon: 'pi pi-bolt', audience: 'emergency', moduleKey: 'urgences-logistique',
    entries: [
      // Appels d'urgence / Dispatch / Missions en onglets.
      { route: '/emergency-regulation', label: 'NAV.EMERGENCY_DISPATCH', icon: 'pi pi-bolt', roles: Roles.COORDINATION },
      // Véhicules / équipement / maintenance / personnel / analyse en onglets.
      { route: '/fleet', label: 'NAV.FLEET', icon: 'pi pi-car', roles: Roles.LOGISTICS },
    ],
  },
  {
    id: 'exams', label: 'NAV.SPACES.EXAMS', icon: 'pi pi-chart-scatter', audience: 'all', moduleKey: 'examens-analyses',
    entries: [
      // Demandes + Résultats + Catalogue de tests + Réactifs + Prestataires.
      { route: '/laboratory-hub', label: 'NAV.LABORATORY', icon: 'pi pi-chart-scatter', roles: Roles.LAB_EXTENDED },
      { route: '/radiology', label: 'NAV.RADIOLOGY', icon: 'pi pi-image', roles: LAB },
    ],
  },
  {
    id: 'pharmacy', label: 'NAV.SPACES.PHARMACY', icon: 'pi pi-shopping-bag', audience: 'pharmacy', moduleKey: 'pharmacie-stock',
    entries: [
      // Caisse + Historique des ventes (onglets).
      { route: '/pharmacy-pos', label: 'NAV.POS', icon: 'pi pi-shopping-cart', roles: PHARMACY },
      { route: '/pharmacy-catalog', label: 'NAV.DRUG_CATALOG', icon: 'pi pi-database', roles: Roles.PHARMACY_EXTENDED },
      // Stock pharmacie + Accessoires + Dépôt + Mouvements + Alertes + Transferts + Prévisions.
      { route: '/stock-hub', label: 'NAV.STOCK', icon: 'pi pi-box', roles: Roles.STOCK },
      { route: '/pharmacy-purchase-orders', label: 'NAV.PURCHASE_ORDERS', icon: 'pi pi-file-edit', roles: Roles.STOCK },
    ],
  },
  {
    id: 'finance', label: 'NAV.SPACES.FINANCE', icon: 'pi pi-credit-card', audience: 'finance', moduleKey: 'finance-facturation',
    entries: [
      // Factures + Devis + Avoirs + Tarifs.
      { route: '/billing-hub', label: 'NAV.BILLING', icon: 'pi pi-credit-card', roles: Roles.FINANCE },
      // Assurances + Pré-autorisations + Tiers-payant.
      { route: '/insurance-hub', label: 'NAV.INSURANCE', icon: 'pi pi-shield', roles: Roles.FINANCE },
      { route: '/debt-recovery', label: 'NAV.DEBT_RECOVERY', icon: 'pi pi-exclamation-circle', roles: Roles.FINANCE },
      // Plan comptable + Écritures + Journal.
      { route: '/accounting-hub', label: 'NAV.ACCOUNTING', icon: 'pi pi-calculator', roles: Roles.FINANCE, groupStart: 'NAV.GROUP_START.ACCOUNTING' },
    ],
  },
  {
    id: 'team', label: 'NAV.SPACES.TEAM', icon: 'pi pi-id-card', audience: 'hr', moduleKey: 'ressources-humaines',
    entries: [
      // L'espace s'ouvre au `gestionnaire` : ses entrées suivent le même groupe de rôles que l'API.
      { route: '/rh-dashboard', label: 'NAV.HR_DASHBOARD', icon: 'pi pi-chart-bar', roles: Roles.HR },
      // Personnel + Médecins + Profils praticiens + Départements.
      { route: '/people-hub', label: 'NAV.STAFF', icon: 'pi pi-id-card', roles: Roles.ADMIN },
      { route: '/shifts', label: 'NAV.TIME_OFF', icon: 'pi pi-calendar', roles: Roles.HR },
      { route: '/payroll', label: 'NAV.PAYROLL', icon: 'pi pi-wallet', roles: Roles.HR },
      { route: '/occupational-health', label: 'NAV.OCCUPATIONAL_HEALTH', icon: 'pi pi-shield', roles: Roles.OCCUPATIONAL_HEALTH, capability: 'occupationalRead' },
    ],
  },
  {
    // Sans module : la messagerie reste visible pour tout le personnel.
    id: 'communication', label: 'NAV.SPACES.COMMUNICATION', icon: 'pi pi-comments', audience: 'all',
    entries: [
      { route: '/secure-messages', label: 'NAV.SECURE_MESSAGES', icon: 'pi pi-envelope', roles: Roles.STAFF_ALL },
      // Mêmes rôles que la route et l'API : l'accueil traite les réclamations
      // (l'ancienne rubrique Administration la lui masquait par effet de bord).
      { route: '/complaints', label: 'NAV.COMPLAINTS', icon: 'pi pi-flag', roles: Roles.COMPLAINTS },
    ],
  },
  {
    id: 'admin', label: 'NAV.SPACES.ADMIN', icon: 'pi pi-cog', audience: 'admin', moduleKey: 'administration',
    entries: [
      // Page à sections (?section=) : établissement, marque, fonctionnement, abonnement, consommation.
      { route: '/organization', label: 'NAV.CLINIC_CONFIGURATION', icon: 'pi pi-building-columns', roles: Roles.ADMIN },
      { route: '/roles', label: 'NAV.ROLES', icon: 'pi pi-lock', roles: Roles.ADMIN },
      { route: '/guided-tours/manage', label: 'NAV.GUIDED_TOURS_MANAGE', icon: 'pi pi-compass', roles: Roles.ADMIN },
      // Clés API + Webhooks + Sauvegardes + Journal d'audit.
      { route: '/integrations-hub', label: 'NAV.INTEGRATIONS', icon: 'pi pi-link', roles: Roles.ADMIN, groupStart: 'NAV.GROUP_START.ADVANCED' },
    ],
  },
  {
    id: 'documents', label: 'NAV.SPACES.DOCUMENTS', icon: 'pi pi-folder-open', audience: 'all', moduleKey: 'ged-documents',
    entries: [
      // Documents médicaux + Documentation + Bibliothèque + Modèles à imprimer.
      { route: '/ged-hub', label: 'NAV.DOCUMENTS', icon: 'pi pi-folder-open', roles: Roles.DOCUMENTS },
      { route: '/forms', label: 'NAV.CUSTOM_FORMS', icon: 'pi pi-list-check', roles: Roles.OPERATIONS, groupStart: 'NAV.GROUP_START.COLLECTION' },
    ],
  },
  {
    id: 'platform', label: 'NAV.SPACES.PLATFORM', icon: 'pi pi-crown', audience: 'super_admin',
    entries: [
      { route: '/super-admin', label: 'NAV.SUPER_ADMIN_OVERVIEW', icon: 'pi pi-crown', roles: ['super_admin'] },
      { route: '/super-admin/organizations', label: 'NAV.ORGANIZATIONS', icon: 'pi pi-building', roles: ['super_admin'] },
      { route: '/super-admin/users', label: 'NAV.USERS', icon: 'pi pi-users', roles: ['super_admin'] },
    ],
  },
];

/** Modules affichés tant que l'organisation n'a jamais configuré Paramètres > Modules. */
const DEFAULT_ENABLED_MODULE_KEYS = [
  'ged-documents',
  'patients-soins',
  'examens-analyses',
  'hospitalisation',
  'pharmacie-stock',
  'finance-facturation',
  'ressources-humaines',
];

export const essentialRoutesForRole = (role?: string): ReadonlySet<string> => new Set((({
  admin: ['/dashboard', '/reception-hub', '/patients', '/consultations-hub', '/laboratory-hub', '/stock-hub', '/billing-hub', '/people-hub', '/organization', '/integrations-hub'],
  super_admin: ['/dashboard', '/super-admin', '/super-admin/organizations', '/super-admin/users'],
  doctor: ['/dashboard', '/reception-hub', '/appointments', '/patients', '/consultations-hub'],
  receptionist: ['/dashboard', '/reception-hub', '/patients', '/appointments'],
  aide_soignante: ['/dashboard', '/reception-hub', '/hospitalization', '/nursing-care', '/patients'],
  laborantin: ['/dashboard', '/laboratory-hub', '/blood-hub', '/calendar'],
  pharmacienne: ['/dashboard', '/pharmacy-pos', '/consultations-hub', '/stock-hub', '/pharmacy-purchase-orders'],
  gestionnaire: ['/dashboard', '/billing-hub', '/stock-hub', '/shifts', '/rh-dashboard'],
} as Record<string, string[]>)[role ?? ''] ?? ['/dashboard']).concat(
  // La messagerie reste visible en mode « Essentiel » : sans elle, l'espace
  // Communication disparaissait du menu pour tout le personnel.
  role && role !== 'super_admin' && role !== 'patient' ? ['/secure-messages'] : [],
));

export function buildNavigation(context: NavigationContext): MenuItem[] {
  const { hasAnyRole } = context;
  const audiences: Record<Audience, boolean> = {
    all: true,
    super_admin: hasAnyRole(['super_admin']),
    pharmacy: hasAnyRole(Roles.PHARMACY_EXTENDED) || hasAnyRole(Roles.FINANCE),
    emergency: hasAnyRole(Roles.COORDINATION),
    finance: hasAnyRole(Roles.FINANCE),
    hr: hasAnyRole([...Roles.HR, 'doctor', 'receptionist']),
    admin: hasAnyRole(Roles.ADMIN),
  };

  // Les modules verrouillés (Vue globale, Administration) sont toujours actifs.
  const enabledModuleKeys = new Set([
    ...(context.enabledModules ?? DEFAULT_ENABLED_MODULE_KEYS),
    ...SIDEBAR_MODULES.filter((mod) => mod.locked).map((mod) => mod.key),
  ]);

  return SPACES.flatMap((space): MenuItem[] => {
    if (!audiences[space.audience]) return [];
    const items = space.entries
      .filter((entry) => {
        const moduleKey = entry.moduleKey ?? space.moduleKey;
        return !moduleKey || enabledModuleKeys.has(moduleKey);
      })
      .map((entry) => ({
        entry,
        item: {
          label: context.t(entry.label),
          icon: entry.icon,
          routerLink: entry.route,
          exact: false,
          itemRoles: [...entry.roles],
          ...(entry.groupStart ? { groupStart: context.t(entry.groupStart) } : {}),
          ...(entry.queryParams ? { queryParams: entry.queryParams(context.role) } : {}),
        } as NavigationItem,
      }))
      .filter(({ entry, item }) => context.itemAllowed(item)
        && (!entry.capability || canClinical(context.role, entry.capability)))
      .map(({ item }) => item);
    return items.length ? [{ id: space.id, label: context.t(space.label), icon: space.icon, expanded: false, items }] : [];
  });
}
